/**
 * Running configurations (Run & Share, Phase 1).
 *
 * One entry per `<repo path> + <configuration name>`. Kept in a module-level
 * Zustand store, not component state, so a dev server keeps running — and
 * keeps its log — when the user leaves the project page and comes back.
 *
 * Lifecycle:
 *   starting  spawned; waiting for the port to accept connections
 *   running   spawned, no port configured (nothing to wait for)
 *   ready     the port answers — "Open preview" is useful now
 *   stopping  terminal_kill sent (it stops the whole process tree), waiting for the exit
 *   exited    the process ended (exitCode when the PTY reported one)
 *   error     never started (bad cwd, program not found, port taken)
 *
 * The process is a terminal session without a pane: output comes through
 * the shared terminal bus (`acquireTerminalBus` + `subscribeTerminal`), and
 * the bus is held for exactly as long as the process lives.
 */
import { create } from 'zustand';
import { appendLog, createLogBuffer } from './runLogBuffer';
import { killProcess, portReady, resolveRunCwd, startProcess } from './runApi';
import { runKey } from './runKeys';

export const PORT_POLL_MS = 700;
/** Give up waiting for the port after this long; the process keeps running. */
export const PORT_WAIT_MS = 5 * 60 * 1000;


export { runKey };

const LIVE = new Set(['starting', 'running', 'ready', 'stopping']);
export const isLive = (run) => !!run && LIVE.has(run.status);

/**
 * Selector: the keys of live runs as ONE string, so a component that only
 * needs "is it running?" re-renders when a run starts or ends — not on every
 * chunk of output (each chunk replaces `runs`). Split with `liveKeySet`.
 */
export const selectLiveKeys = (s) =>
  Object.keys(s.runs)
    .filter((k) => isLive(s.runs[k]))
    .sort()
    .join('\n');
export const liveKeySet = (joined) => new Set(joined ? joined.split('\n') : []);

// Per-run resources that must not live in React state.
const handles = new Map(); // key -> { sid, release, unsubscribe, timer }
let attempts = 0;

export const useRunStore = create(() => ({ runs: {} }));

function patch(key, fields) {
  useRunStore.setState((s) => {
    const prev = s.runs[key];
    if (!prev) return s;
    return { runs: { ...s.runs, [key]: { ...prev, ...fields } } };
  });
}

function appendChunk(key, chunk) {
  useRunStore.setState((s) => {
    const prev = s.runs[key];
    if (!prev) return s;
    return { runs: { ...s.runs, [key]: { ...prev, log: appendLog(prev.log, chunk) } } };
  });
}

function cleanup(key) {
  const h = handles.get(key);
  if (!h) return;
  handles.delete(key);
  if (h.timer) clearTimeout(h.timer);
  h.unsubscribe?.();
  h.release?.();
}

function pollPort(key, port, startedAt) {
  const h = handles.get(key);
  if (!h) return;
  h.timer = setTimeout(async () => {
    h.timer = null;
    const run = useRunStore.getState().runs[key];
    if (!run || run.status !== 'starting' || handles.get(key) !== h) return;
    let ok = false;
    try {
      ok = await portReady(port);
    } catch {
      ok = false;
    }
    const now = useRunStore.getState().runs[key];
    if (!now || now.status !== 'starting' || handles.get(key) !== h) return;
    if (ok) patch(key, { status: 'ready' });
    else if (Date.now() - startedAt < PORT_WAIT_MS) pollPort(key, port, startedAt);
    else patch(key, { status: 'running', portTimedOut: true });
  }, PORT_POLL_MS);
}

/**
 * Start `config` from the repo at `repoPath`. Resolves once the process is
 * spawned (or failed to); never throws — failures land in the run's status.
 * The caller has already done the first-run approval.
 */
export async function startRun(repoPath, config) {
  const key = runKey(repoPath, config.name);
  if (isLive(useRunStore.getState().runs[key])) return;
  cleanup(key);
  const startedAt = Date.now();
  attempts += 1;
  const attempt = attempts;
  useRunStore.setState((s) => ({
    runs: {
      ...s.runs,
      [key]: { attempt, status: 'starting', port: config.port || null, startedAt, log: createLogBuffer(), exitCode: null, error: null },
    },
  }));
  // A stop (or a newer start) while we awaited makes this attempt stale.
  const stale = () => {
    const run = useRunStore.getState().runs[key];
    return !run || run.attempt !== attempt || run.status !== 'starting';
  };
  const fail = (error) => {
    if (stale()) return;
    cleanup(key);
    patch(key, { status: 'error', error: String(error?.message || error) });
  };

  if (config.port) {
    let busy = false;
    try {
      busy = await portReady(config.port);
    } catch {
      busy = false;
    }
    if (busy) {
      fail('port_in_use');
      return;
    }
  }
  let cwd;
  try {
    cwd = await resolveRunCwd(repoPath, config.cwd);
  } catch (e) {
    fail(e);
    return;
  }
  if (stale()) return;

  const h = { sid: null, release: null, unsubscribe: null, timer: null };
  handles.set(key, h);
  try {
    // Hold the bus BEFORE spawning, so nothing the process prints is missed.
    // Lazy: the bus imports the Tauri event API, which the project page must
    // not load eagerly (tests/tauriImportsAreLazy, jest's untranspiled ESM).
    const { acquireTerminalBus, subscribeTerminal } = await import('../../../hooks/terminalOutputBus');
    const release = await acquireTerminalBus();
    if (handles.get(key) !== h) {
      // Stopped while the bus was starting: let go of it and spawn nothing.
      release();
      return;
    }
    h.release = release;
    const res = await startProcess(cwd, config);
    if (handles.get(key) !== h) {
      // Stopped (or restarted) while we were spawning.
      killProcess(res.id).catch(() => {});
      return;
    }
    h.sid = res.id;
    patch(key, { sid: res.id, status: config.port ? 'starting' : 'running' });
    const unsubscribe = subscribeTerminal(res.id, {
      onData: (chunk) => appendChunk(key, chunk),
      onExit: (code) => {
        if (handles.get(key) !== h) return;
        // A Stop the user asked for is "Stopped", whatever code the kill produced.
        const stopped = useRunStore.getState().runs[key]?.status === 'stopping';
        cleanup(key);
        patch(key, { status: 'exited', exitCode: !stopped && typeof code === 'number' ? code : null, sid: null });
      },
    });
    // An exit that arrived before we subscribed is replayed INSIDE subscribeTerminal,
    // so cleanup may already have run: then drop the subscription ourselves.
    if (handles.get(key) === h) h.unsubscribe = unsubscribe;
    else unsubscribe();
    if (config.port && handles.get(key) === h) pollPort(key, config.port, startedAt);
  } catch (e) {
    if (handles.get(key) === h) {
      cleanup(key);
      patch(key, { status: 'error', error: String(e?.message || e) });
    }
  }
}

/**
 * Run one setup command (Check setup's fixes: `npm ci`, `mix deps.get`,
 * `mix ecto.setup` …) under `key`, exactly like a configuration: the same
 * `terminal_create` launch path with the process-tree runner, output in that
 * key's log, Stop works. No port. Resolves when the process ends:
 * `{ ok, exitCode, error }` — ok only for exit code 0. Never throws.
 * The caller has already done the first-run approval.
 *
 * `cmd` = { program, args, cwd } (cwd repo-relative); `label` is shown as the
 * row status ("Running npm ci…").
 */
export async function runTask(repoPath, key, cmd, label) {
  if (isLive(useRunStore.getState().runs[key])) return { ok: false, error: 'busy' };
  cleanup(key);
  attempts += 1;
  const attempt = attempts;
  useRunStore.setState((s) => ({
    runs: {
      ...s.runs,
      [key]: { attempt, status: 'running', task: label || cmd.program, port: null, startedAt: Date.now(), log: createLogBuffer(), exitCode: null, error: null },
    },
  }));
  const current = () => useRunStore.getState().runs[key]?.attempt === attempt;
  let cwd;
  try {
    cwd = await resolveRunCwd(repoPath, cmd.cwd || null);
  } catch (e) {
    if (current()) patch(key, { status: 'error', error: String(e?.message || e) });
    return { ok: false, error: String(e?.message || e) };
  }
  return new Promise((resolve) => {
    const h = { sid: null, release: null, unsubscribe: null, timer: null };
    handles.set(key, h);
    const fail = (e) => {
      if (handles.get(key) === h) {
        cleanup(key);
        patch(key, { status: 'error', error: String(e?.message || e) });
      }
      resolve({ ok: false, error: String(e?.message || e) });
    };
    (async () => {
      const { acquireTerminalBus, subscribeTerminal } = await import('../../../hooks/terminalOutputBus');
      const release = await acquireTerminalBus();
      if (handles.get(key) !== h) {
        release();
        resolve({ ok: false, error: 'stopped' });
        return;
      }
      h.release = release;
      const res = await startProcess(cwd, { runtimeExecutable: cmd.program, runtimeArgs: cmd.args || [], env: {} });
      if (handles.get(key) !== h) {
        killProcess(res.id).catch(() => {});
        resolve({ ok: false, error: 'stopped' });
        return;
      }
      h.sid = res.id;
      patch(key, { sid: res.id });
      const unsubscribe = subscribeTerminal(res.id, {
        onData: (chunk) => appendChunk(key, chunk),
        onExit: (code) => {
          if (handles.get(key) !== h) return;
          const stopped = useRunStore.getState().runs[key]?.status === 'stopping';
          cleanup(key);
          const exitCode = typeof code === 'number' ? code : null;
          patch(key, { status: 'exited', exitCode: stopped ? null : exitCode, sid: null });
          resolve({ ok: !stopped && exitCode === 0, exitCode });
        },
      });
      if (handles.get(key) === h) h.unsubscribe = unsubscribe;
      else unsubscribe();
    })().catch(fail);
  });
}

/** Stop a run. The status becomes `exited` when the PTY reports the exit. */
export async function stopRun(key) {
  const run = useRunStore.getState().runs[key];
  const h = handles.get(key);
  if (!run || !isLive(run)) return;
  if (!h?.sid) {
    // Still spawning: forget it; startRun kills the process once it has an id.
    cleanup(key);
    patch(key, { status: 'exited', exitCode: null });
    return;
  }
  patch(key, { status: 'stopping' });
  try {
    await killProcess(h.sid);
  } catch {
    // Already gone: the exit event (or this) settles the row.
    if (handles.get(key) === h) {
      cleanup(key);
      patch(key, { status: 'exited', exitCode: null, sid: null });
    }
  }
}

/** Stop every live run whose key `match`es; resolves when every stop was sent. */
function stopWhere(match) {
  const keys = Object.keys(useRunStore.getState().runs).filter((k) => match(k) && isLive(useRunStore.getState().runs[k]));
  return Promise.all(keys.map((k) => stopRun(k))).then(() => keys.length);
}

/**
 * Stop every running configuration: the user signs out (or the session ends).
 * The app quitting is handled in Rust (`RunEvent::Exit` → `stop_all_trees`):
 * no webview code is guaranteed to run while the process exits.
 */
export const stopAllRuns = () => stopWhere(() => true);

/** Stop the runs of one repo folder (its folder is forgotten, or the repo leaves the project). */
export const stopRunsUnder = (repoPath) => (repoPath ? stopWhere((k) => k.startsWith(runKey(repoPath, ''))) : Promise.resolve(0));

// Both sign-out paths end in `auth:logout`: the explicit logout() dispatches
// it, and the soft one (401 interceptor, session cap) IS it.
if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    stopAllRuns().catch(() => {});
  });
}

export function clearRunLog(key) {
  patch(key, { log: createLogBuffer() });
}

/** Test hook. */
export function resetRunStoreForTests() {
  [...handles.keys()].forEach(cleanup);
  useRunStore.setState({ runs: {} });
}
